/**
 * Document Editor Types
 * Type definitions for document editor UI components.
 *
 * Uses ResolvedDocument directly from block types - no legacy types.
 */
import type {
	ResolvedDocument,
	ResolvedDocumentChild,
	ResolvedSectionChild,
	PlayReferenceBlock,
	ResolvedBlock,
	SectionBlock,
} from '@/types/blocks'

// Re-export block types for convenience
export type { ResolvedDocument, ResolvedDocumentChild, ResolvedSectionChild }

/**
 * Extended section properties including spatial data stored in JSONB.
 * Base SectionBlock.properties plus optional layout configuration.
 */
interface SectionPropertiesWithLayout {
	name: string
	color?: string
	collapsed?: boolean
	layout?: { mode: 'column' | 'row' | 'free' | 'grid'; gridColumns?: number; gridRows?: number }
}

// ========== UI Types ==========

/**
 * UI-friendly section representation derived from ResolvedDocument.
 * Maps block structure to editor-friendly format.
 */
export interface SectionItem {
	id: string
	blockId: string // Original block ID
	name: string
	color: string | null
	collapsed: boolean
	children: ResolvedSectionChild[]
	// Grid layout configuration
	gridColumns?: number
	gridRows?: number
}

/**
 * UI-friendly play reference representation.
 * Derived from ResolvedSectionChild with type === 'play_reference'.
 */
export interface PlayItem {
	id: string // Block reference ID
	blockId: string // PlayReferenceBlock.id
	playId: string // Referenced play block ID
	name: string
	playType: 'run' | 'pass' | null
	formationName: string | null
	personnelName: string | null
	hashPosition: string
	displayOrder: number
	customNumber: number | null
	notes: string | null
	wristbandIncluded: boolean
	/** Whether this is a broken reference (target play deleted) */
	isBrokenReference: boolean
}

// ========== Helper Functions ==========

/**
 * Convert a SectionBlock to a SectionItem.
 * Shared conversion logic for consistent section handling.
 */
export function sectionBlockToItem(section: SectionBlock, children: ResolvedSectionChild[]): SectionItem {
	const props = section.properties as SectionPropertiesWithLayout
	return {
		id: section.id,
		blockId: section.id,
		name: props.name,
		color: props.color ?? null,
		collapsed: props.collapsed ?? false,
		children,
		gridColumns: props.layout?.gridColumns,
		gridRows: props.layout?.gridRows,
	}
}

/**
 * Extract sections from a ResolvedDocument.
 * Filters children for type === 'section'.
 */
export function getSectionsFromDocument(doc: ResolvedDocument | null): SectionItem[] {
	if (!doc) return []

	return doc.children
		.filter((child): child is Extract<ResolvedDocumentChild, { type: 'section' }> =>
			child.type === 'section'
		)
		.map((child) => sectionBlockToItem(child.section, child.children))
}

/**
 * Convert a play_reference child to a PlayItem.
 * Handles broken references gracefully.
 */
export function resolvedBlockToPlayItem(
	block: PlayReferenceBlock,
	resolvedBlock: ResolvedBlock | null,
	displayOrder: number
): PlayItem {
	const isBrokenReference = !resolvedBlock
	return {
		id: block.id,
		blockId: block.id,
		playId: resolvedBlock?.block.id ?? (block.properties?.targetId as string) ?? '',
		name: isBrokenReference ? 'Missing Play' : ((resolvedBlock?.block.properties as { name?: string })?.name ?? 'Unnamed Play'),
		playType: null,
		formationName: resolvedBlock?.formationBlock?.properties?.name ?? null,
		personnelName: null,
		hashPosition: 'middle',
		displayOrder,
		customNumber: null,
		notes: null,
		wristbandIncluded: false,
		isBrokenReference,
	}
}

/**
 * Get plays from a section's children.
 * Handles broken play references with placeholders instead of crashing.
 */
export function getPlaysFromSection(section: SectionItem): PlayItem[] {
	return section.children
		.filter((child): child is Extract<ResolvedSectionChild, { type: 'play_reference' }> =>
			child.type === 'play_reference'
		)
		.map((child, index) => resolvedBlockToPlayItem(child.block, child.resolvedBlock, index))
}
